import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from 'axios';

// Base URL desde el proxy de package.json
const API_URL = process.env.REACT_APP_API_URL || '';

// Async thunks para operaciones de tickets
export const crearTicket = createAsyncThunk(
  'tickets/crearTicket',
  async ({ ticketData, archivos }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      
      // Agregar datos del ticket
      Object.keys(ticketData).forEach(key => {
        formData.append(key, ticketData[key]);
      });
      
      // Agregar archivos si existen
      if (archivos && archivos.length > 0) {
        archivos.forEach(archivo => {
          formData.append('archivos', archivo.file);
        });
      }
      
      const response = await axios.post(`${API_URL}/api/v1/tickets/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Ticket creado exitosamente');
      return response.data;
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear ticket';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const listarTickets = createAsyncThunk(
  'tickets/listarTickets',
  async ({ farmacia_id, estatus, categoria }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (farmacia_id) params.append('farmacia_id', farmacia_id);
      if (estatus) params.append('estatus', estatus);
      if (categoria) params.append('categoria', categoria);
      
      const response = await axios.get(`${API_URL}/api/v1/tickets/?${params}`);
      return response.data;
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al listar tickets';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const obtenerTicket = createAsyncThunk(
  'tickets/obtenerTicket',
  async (ticketId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/tickets/${ticketId}`);
      return response.data;
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al obtener ticket';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const agregarMensaje = createAsyncThunk(
  'tickets/agregarMensaje',
  async ({ ticketId, mensajeData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/api/v1/tickets/${ticketId}/mensajes`, mensajeData);
      toast.success('Mensaje enviado');
      return response.data;
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al enviar mensaje';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const cambiarEstatusTicket = createAsyncThunk(
  'tickets/cambiarEstatus',
  async ({ ticketId, nuevoEstatus }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/api/v1/tickets/${ticketId}/estatus?nuevo_estatus=${encodeURIComponent(nuevoEstatus)}`);
      toast.success(`Estado cambiado a ${nuevoEstatus}`);
      return response.data;
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al cambiar estado';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const valorarRespuesta = createAsyncThunk(
  'tickets/valorarRespuesta',
  async ({ mensajeId, valoracion }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/api/v1/tickets/mensajes/${mensajeId}/valorar`, valoracion);
      toast.success('Respuesta valorada');
      return response.data;
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al valorar respuesta';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const listarTodosTickets = createAsyncThunk(
  'tickets/listarTodosTickets',
  async ({ farmacia_id, estatus, categoria, prioridad, search, limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (farmacia_id) params.append('farmacia_id', farmacia_id);
      if (estatus) params.append('estatus', estatus);
      if (categoria) params.append('categoria', categoria);
      if (prioridad) params.append('prioridad', prioridad);
      if (search) params.append('search', search);
      params.append('limit', limit);
      params.append('offset', offset);
      
      const response = await axios.get(`${API_URL}/api/v1/tickets/admin/todos?${params}`);
      return response.data;
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al listar todos los tickets';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const marcarComoDuplicado = createAsyncThunk(
  'tickets/marcarComoDuplicado',
  async ({ ticketId, ticketOriginalId }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/v1/tickets/${ticketId}/duplicado?ticket_original_id=${ticketOriginalId}`
      );
      toast.success('Ticket marcado como duplicado');
      return { ticketId, ticketOriginalId, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al marcar como duplicado';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const eliminarTicket = createAsyncThunk(
  'tickets/eliminarTicket',
  async (ticketId, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/api/v1/tickets/${ticketId}`);
      toast.success('Ticket eliminado exitosamente');
      return ticketId;
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al eliminar ticket';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Estado inicial
const initialState = {
  tickets: [],
  totalTickets: 0,
  ticketActual: null,
  loading: false,
  error: null,
  filtros: {
    farmacia_id: '',
    estatus: '',
    categoria: '',
    prioridad: ''
  },
  creandoTicket: false,
  enviandoMensaje: false,
  eliminandoTicket: false,
  // Estadísticas
  estadisticas: {
    total: 0,
    abiertos: 0,
    cerrados: 0,
    enProgreso: 0,
    porCategoria: {
      Soporte: 0,
      Facturacion: 0
    },
    porPrioridad: {
      Baja: 0,
      Media: 0,
      Alta: 0,
      Urgente: 0
    }
  }
};

// Slice de tickets
const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    limpiarError: (state) => {
      state.error = null;
    },
    setFiltros: (state, action) => {
      state.filtros = { ...state.filtros, ...action.payload };
    },
    limpiarFiltros: (state) => {
      state.filtros = {
        farmacia_id: '',
        estatus: '',
        categoria: '',
        prioridad: ''
      };
    },
    limpiarTicketActual: (state) => {
      state.ticketActual = null;
    },
    actualizarTicketEnLista: (state, action) => {
      const { ticketId, actualizaciones } = action.payload;
      const index = state.tickets.findIndex(ticket => ticket.id === ticketId);
      if (index !== -1) {
        state.tickets[index] = { ...state.tickets[index], ...actualizaciones };
      }
    },
    calcularEstadisticas: (state) => {
      state.estadisticas = {
        total: state.tickets.length,
        abiertos: state.tickets.filter(t => t.estatus === 'Abierto').length,
        cerrados: state.tickets.filter(t => t.estatus === 'Cerrado').length,
        enProgreso: state.tickets.filter(t => t.estatus === 'En Progreso').length
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Crear ticket
      .addCase(crearTicket.pending, (state) => {
        state.creandoTicket = true;
        state.error = null;
      })
      .addCase(crearTicket.fulfilled, (state, action) => {
        state.creandoTicket = false;
        state.tickets.unshift(action.payload);
        // Calcular estadísticas
        state.estadisticas = {
          total: state.tickets.length,
          abiertos: state.tickets.filter(t => t.estatus === 'Abierto').length,
          enProgreso: state.tickets.filter(t => t.estatus === 'En Progreso').length,
          cerrados: state.tickets.filter(t => t.estatus === 'Cerrado').length,
          porCategoria: {
            Soporte: state.tickets.filter(t => t.categoria === 'Soporte').length,
            Facturacion: state.tickets.filter(t => t.categoria === 'Facturacion').length
          },
          porPrioridad: {
            Baja: state.tickets.filter(t => t.prioridad === 'Baja').length,
            Media: state.tickets.filter(t => t.prioridad === 'Media').length,
            Alta: state.tickets.filter(t => t.prioridad === 'Alta').length,
            Urgente: state.tickets.filter(t => t.prioridad === 'Urgente').length
          }
        };
      })
      .addCase(crearTicket.rejected, (state, action) => {
        state.creandoTicket = false;
        state.error = action.payload;
      })
      
      // Listar tickets
      .addCase(listarTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listarTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload;
        // Calcular estadísticas
        state.estadisticas = {
          total: state.tickets.length,
          abiertos: state.tickets.filter(t => t.estatus === 'Abierto').length,
          enProgreso: state.tickets.filter(t => t.estatus === 'En Progreso').length,
          cerrados: state.tickets.filter(t => t.estatus === 'Cerrado').length,
          porCategoria: {
            Soporte: state.tickets.filter(t => t.categoria === 'Soporte').length,
            Facturacion: state.tickets.filter(t => t.categoria === 'Facturacion').length
          },
          porPrioridad: {
            Baja: state.tickets.filter(t => t.prioridad === 'Baja').length,
            Media: state.tickets.filter(t => t.prioridad === 'Media').length,
            Alta: state.tickets.filter(t => t.prioridad === 'Alta').length,
            Urgente: state.tickets.filter(t => t.prioridad === 'Urgente').length
          }
        };
      })
      .addCase(listarTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Obtener ticket
      .addCase(obtenerTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(obtenerTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.ticketActual = action.payload;
      })
      .addCase(obtenerTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Agregar mensaje
      .addCase(agregarMensaje.pending, (state) => {
        state.enviandoMensaje = true;
        state.error = null;
      })
      .addCase(agregarMensaje.fulfilled, (state, action) => {
        state.enviandoMensaje = false;
        if (state.ticketActual) {
          state.ticketActual.mensajes.push(action.payload);
        }
      })
      .addCase(agregarMensaje.rejected, (state, action) => {
        state.enviandoMensaje = false;
        state.error = action.payload;
      })
      
      // Cambiar estatus
      .addCase(cambiarEstatusTicket.fulfilled, (state, action) => {
        const index = state.tickets.findIndex(ticket => ticket.id === action.payload.id);
        if (index !== -1) {
          state.tickets[index] = action.payload;
        }
        if (state.ticketActual && state.ticketActual.id === action.payload.id) {
          state.ticketActual = action.payload;
        }
        // Calcular estadísticas
        state.estadisticas = {
          total: state.tickets.length,
          abiertos: state.tickets.filter(t => t.estatus === 'Abierto').length,
          enProgreso: state.tickets.filter(t => t.estatus === 'En Progreso').length,
          cerrados: state.tickets.filter(t => t.estatus === 'Cerrado').length,
          porCategoria: {
            Soporte: state.tickets.filter(t => t.categoria === 'Soporte').length,
            Facturacion: state.tickets.filter(t => t.categoria === 'Facturacion').length
          },
          porPrioridad: {
            Baja: state.tickets.filter(t => t.prioridad === 'Baja').length,
            Media: state.tickets.filter(t => t.prioridad === 'Media').length,
            Alta: state.tickets.filter(t => t.prioridad === 'Alta').length,
            Urgente: state.tickets.filter(t => t.prioridad === 'Urgente').length
          }
        };
      })
      
      // Valorar respuesta
      .addCase(valorarRespuesta.fulfilled, (state, action) => {
        if (state.ticketActual) {
          const mensajeIndex = state.ticketActual.mensajes.findIndex(m => m.id === action.payload.id);
          if (mensajeIndex !== -1) {
            state.ticketActual.mensajes[mensajeIndex] = action.payload;
          }
        }
      })
      
      // Listar todos los tickets (admin)
      .addCase(listarTodosTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listarTodosTickets.fulfilled, (state, action) => {
        state.loading = false;
        // El backend retorna [tickets, total]
        const [tickets, total] = action.payload;
        state.tickets = tickets;
        state.totalTickets = total;
        // Calcular estadísticas basadas en los tickets actuales
        state.estadisticas = {
          total: tickets.length,
          abiertos: tickets.filter(t => t.estatus === 'Abierto').length,
          enProgreso: tickets.filter(t => t.estatus === 'En Progreso').length,
          cerrados: tickets.filter(t => t.estatus === 'Cerrado').length,
          porCategoria: {
            Soporte: tickets.filter(t => t.categoria === 'Soporte').length,
            Facturacion: tickets.filter(t => t.categoria === 'Facturacion').length
          },
          porPrioridad: {
            Baja: tickets.filter(t => t.prioridad === 'Baja').length,
            Media: tickets.filter(t => t.prioridad === 'Media').length,
            Alta: tickets.filter(t => t.prioridad === 'Alta').length,
            Urgente: tickets.filter(t => t.prioridad === 'Urgente').length
          }
        };
      })
      .addCase(listarTodosTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Eliminar ticket
      .addCase(eliminarTicket.pending, (state) => {
        state.eliminandoTicket = true;
        state.error = null;
      })
      .addCase(eliminarTicket.fulfilled, (state, action) => {
        state.eliminandoTicket = false;
        state.tickets = state.tickets.filter(t => t.id !== action.payload);
        state.totalTickets = Math.max(0, state.totalTickets - 1);
        state.estadisticas = {
          total: state.tickets.length,
          abiertos: state.tickets.filter(t => t.estatus === 'Abierto').length,
          enProgreso: state.tickets.filter(t => t.estatus === 'En Progreso').length,
          cerrados: state.tickets.filter(t => t.estatus === 'Cerrado').length,
          porCategoria: {
            Soporte: state.tickets.filter(t => t.categoria === 'Soporte').length,
            Facturacion: state.tickets.filter(t => t.categoria === 'Facturacion').length
          },
          porPrioridad: {
            Baja: state.tickets.filter(t => t.prioridad === 'Baja').length,
            Media: state.tickets.filter(t => t.prioridad === 'Media').length,
            Alta: state.tickets.filter(t => t.prioridad === 'Alta').length,
            Urgente: state.tickets.filter(t => t.prioridad === 'Urgente').length
          }
        };
      })
      .addCase(eliminarTicket.rejected, (state, action) => {
        state.eliminandoTicket = false;
        state.error = action.payload;
      })
      
      // Marcar como duplicado
      .addCase(marcarComoDuplicado.fulfilled, (state, action) => {
        const { ticketId, ticketOriginalId } = action.payload;
        const index = state.tickets.findIndex(t => t.id === ticketId);
        if (index !== -1) {
          state.tickets[index].estatus = 'Duplicado';
          state.tickets[index].duplicado_de = ticketOriginalId;
        }
        if (state.ticketActual && state.ticketActual.id === ticketId) {
          state.ticketActual.estatus = 'Duplicado';
          state.ticketActual.duplicado_de = ticketOriginalId;
        }
      });
  },
});

// Exportar acciones
export const {
  limpiarError,
  setFiltros,
  limpiarFiltros,
  limpiarTicketActual,
  actualizarTicketEnLista,
  calcularEstadisticas
} = ticketsSlice.actions;

// Selectors
export const selectTickets = (state) => state.Tickets.tickets;
export const selectTotalTickets = (state) => state.Tickets.totalTickets;
export const selectTicketActual = (state) => state.Tickets.ticketActual;
export const selectLoading = (state) => state.Tickets.loading;
export const selectError = (state) => state.Tickets.error;
export const selectFiltros = (state) => state.Tickets.filtros;
export const selectCreandoTicket = (state) => state.Tickets.creandoTicket;
export const selectEnviandoMensaje = (state) => state.Tickets.enviandoMensaje;
export const selectEliminandoTicket = (state) => state.Tickets.eliminandoTicket;
export const selectEstadisticas = (state) => state.Tickets.estadisticas;

// Exportar el reducer
export default ticketsSlice.reducer;
